import { Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'

interface Props {
  current_plan: string
}

const FEATURES = [
  {
    title: 'Track unlimited investors',
    desc: 'Capture each capital partner — family, business partners, fund LPs — with TIN, contact info, and capital commitment.',
  },
  {
    title: 'Pro-rata distribution math',
    desc: 'Every payment automatically splits interest and principal across investors based on their ownership percent.',
  },
  {
    title: 'Annual investor statements (PDF)',
    desc: 'One-click year-end statements per investor or bulk ZIP for tax season — investor name, capital deployed, distributions.',
  },
  {
    title: 'K-1 data export (CSV)',
    desc: 'Export the data your accountant needs to file Schedule K-1s for partnership-treated capital arrangements.',
  },
  {
    title: 'Investor portal access',
    desc: 'Invite investors to a read-only portal where they can see their participations, distributions, and download statements.',
  },
]

export default function InvestorsUpgradeTeaser({ current_plan }: Props) {
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full mb-4">
            Fund Plan
          </span>
          <h1 className="text-3xl font-bold text-gray-900">Manage capital partners with the Fund plan</h1>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            Investor management, pro-rata distributions, year-end statements, K-1 exports, and a self-serve investor portal —
            built for lenders deploying capital from multiple partners.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <ul className="space-y-4">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex items-start gap-3">
                <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                  <svg className="w-3 h-3 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{f.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500 mb-3">
            You're currently on the <strong className="text-gray-900">{current_plan.charAt(0).toUpperCase() + current_plan.slice(1)}</strong> plan.
          </p>
          <Link
            href="/billing"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            Upgrade to Fund
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
